import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ReviewApplyOption } from '@common/reviews/enums/review-apply-option.enum';
import { ReviewApplyResult } from '@common/reviews/models/review-apply-result.model';
import { ReviewApplyState } from '@common/reviews/models/review-apply-state.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { ReviewApplyProgressService } from '@portal-core/reviews/review-packages/services/review-apply-progress.service';
import { ReviewPackagesService } from '@portal-core/reviews/review-packages/services/review-packages.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { map, Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export interface ReviewPackageWorkspaceMergeProgressDialogData {
  reviewPackageId: number;
  reviewPackageFiles: ReviewFile[];
  action: ReviewApplyOption;
  mergeUserId: string;
}

export type ReviewPackageWorkspaceMergeProgressDialogResult = MadCloudResult<ReviewApplyResult>;

@Component({
  selector: 'mc-review-package-workspace-merge-progress-dialog',
  templateUrl: './review-package-workspace-merge-progress-dialog.component.html',
  styleUrls: ['./review-package-workspace-merge-progress-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class ReviewPackageWorkspaceMergeProgressDialogComponent extends DialogBase<ReviewPackageWorkspaceMergeProgressDialogComponent, ReviewPackageWorkspaceMergeProgressDialogResult> implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '55rem'
  };

  reviewPackageId: number;
  reviewPackageFiles: ReviewFile[];
  action: ReviewApplyOption;
  mergeUserId: string;

  /** How many of the package's review files have completed their merge, as a 0-100 percentage. */
  progressPercent$: Observable<number>;

  private applyStateSubscription: Subscription;

  constructor(@Inject(MAT_DIALOG_DATA) private data: ReviewPackageWorkspaceMergeProgressDialogData,
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<ReviewPackageWorkspaceMergeProgressDialogComponent, ReviewPackageWorkspaceMergeProgressDialogResult>,
    private reviewApplyProgressService: ReviewApplyProgressService,
    private reviewPackagesService: ReviewPackagesService
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.reviewPackageId = this.data.reviewPackageId;
    this.reviewPackageFiles = this.data.reviewPackageFiles;
    this.action = this.data.action;
    this.mergeUserId = this.data.mergeUserId;

    this.reviewApplyProgressService.clear();

    this.progressPercent$ = this.reviewApplyProgressService.applyState$.pipe(
      map(state => ReviewApplyProgressService.aggregatePercent(state))
    );

    // Read the review package once (as opposed to getItemById$) since the properties used here -
    // Id, BranchName, LicenseId, ProjectId - cannot change on the client, and we only want to kick
    // off the apply once rather than on every store update to this item.
    const reviewPackage = this.reviewPackagesService.getItemById(this.reviewPackageId);

    // Only close the dialog once the apply reaches a terminal state - the initial 202/409 response
    // just means the apply is queued/already running, not that it's finished.
    this.applyStateSubscription = this.reviewApplyProgressService.applyState$.subscribe(state => {
      if (state?.reviewPackageId === reviewPackage.Id && (state.status === 'completed' || state.status === 'failed')) {
        this.closeDialog(this.toDialogResult(state));
      }
    });

    this.reviewApplyProgressService.startApply({
      clientId: uuidv4(),
      option: this.action,
      reviewPackageId: reviewPackage.Id,
      userId: this.mergeUserId
    }, {
      branchName: reviewPackage.BranchName,
      licenseId: reviewPackage.LicenseId,
      projectId: reviewPackage.ProjectId,
      totalFiles: this.reviewPackageFiles.length
    });
  }

  private toDialogResult(state: ReviewApplyState): ReviewPackageWorkspaceMergeProgressDialogResult {
    const failedReviews = state.result?.fileResults.filter(fileResult => fileResult.status === 'failed');
    return state.status === 'completed' && failedReviews != undefined && failedReviews.length == 0
      ? { Succeeded: true, Errors: null, Result: state.result }
      : {
        Succeeded: false,
        Errors: [{
          Code: ErrorCode.UpdateError,
          ExceptionMessage: failedReviews?.length ? `Failed to merge the following reviews: ${failedReviews.map(review => review.path).join(', ')}.` : '',
          InnerExceptionMessage: failedReviews?.length ? `${failedReviews.map(review => review.error).join(', ')}.` : '',
          Message: state.error?.message ?? 'Unable to apply the review package changes.'
        }]
      };
  }
}
